import moment from 'moment';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { getAxiosWithAuth } from '@/lib/axios';
import client from '@/lib/feathers';
import {
  RealtimeMessages,
  realtimeMessagesSchema,
} from '@/types/database/realtimeMessages';

import { getSendMessage } from './helpers';

const useMessages = (associatedEntity: string, entityId: string) => {
  const [messages, setMessages] = useState<RealtimeMessages>([]);
  const [currentOldestMessageTime, setCurrentOldestMessageTime] = useState(
    new Date().toISOString()
  );
  const [keepFetching, setKeepFetching] = useState(true);

  const { data: dataBeforeCurrentOldestMessages } = useSWR(
    [currentOldestMessageTime, keepFetching],
    async ([time, keepFetching]) => {
      if (!keepFetching) {
        return;
      }
      const axios = getAxiosWithAuth();
      const { data } = await axios.get('/ezcon/api/realtimeMessages', {
        params: {
          associatedEntity,
          entityId,
          lastTime: time,
        },
      });
      const valid = realtimeMessagesSchema.safeParse(data);
      if (!valid.success) {
        throw new Error(valid.error.message);
      }
      if (data.length === 0) {
        setKeepFetching(false);
        return;
      }
      const returnValue = valid.data.sort(
        (a, b) => b.createdTime - a.createdTime
      );
      return returnValue;
    }
  );

  useEffect(() => {
    if (dataBeforeCurrentOldestMessages) {
      setMessages((current) => current.concat(dataBeforeCurrentOldestMessages));
    }
  }, [dataBeforeCurrentOldestMessages]);

  useEffect(() => {
    const io = client.io;
    const basicParams = (type: 'connect' | 'disconnect') => [
      'joinOrLeaveRealtimeMessageChat',
      {
        associatedEntity,
        entityId,
        type,
      },
      () => {},
    ];
    io.emit(...basicParams('connect'));
    io.on(
      'realtimeMessages',
      (message: unknown, callback: (ack: string) => void) => {
        if (typeof message !== 'string') {
          return;
        }
        try {
          const valid = realtimeMessagesSchema.element.safeParse(
            JSON.parse(message)
          );
          if (valid.success) {
            setMessages((current) => {
              return [valid.data, ...current];
            });
            callback('success');
            return;
          }
          console.error('parse fail', valid.error);
        } catch (error) {
          console.error('parse fail', error);
        }
      }
    );
    return () => {
      io.emit(...basicParams('disconnect'));
      io.off('realtimeMessages');
    };
  }, [associatedEntity, entityId]);

  const sendMessage = getSendMessage({ associatedEntity, entityId });

  return {
    messages,
    sendMessage,
    fetchMore: () => {
      if (keepFetching) {
        const currentOldestMessage = messages.at(-1);
        if (currentOldestMessage) {
          setCurrentOldestMessageTime(
            moment(currentOldestMessage.createdTime).toISOString()
          );
        }
      }
    },
  };
};

type MessagesInstance = ReturnType<typeof useMessages>;

export default useMessages;
export type { MessagesInstance };
